import { Link } from '@ui/components/Link'
import { Tooltip } from '@ui/components/Tooltip'
import { DateTime } from '@ui/v2/Table/cells/DateTime'
import { VIEW_MORE_ARROW_COL_KEY } from '@ui/v2/Table/hooks/useTableSearch'
import { BaseColumn, BaseColumnCtx } from '@ui/v2/Table/types'

import { getCompanyName } from '@utils/companyName.utils'
import { formatDate } from '@utils/date.utils'
import { placeholder } from '@utils/placeholder'
import { addSwapFreePostfix } from '@utils/text'

import { AccountSummaryDto } from '@model/AccountSummaryDto'
import { PlanValuesKeys } from '@model/MultiTierDto'

import { IBClientsTableSortKeys } from './IBClientsTable'

export interface ClientColumnCtx extends BaseColumnCtx {
  formatNumber: (v?: number) => string
}
export type ClientColumn = BaseColumn<
  AccountSummaryDto,
  ClientColumnCtx,
  IBClientsTableSortKeys | typeof VIEW_MORE_ARROW_COL_KEY
>

export const clientColumns: ClientColumn[] = [
  {
    id: 'Login',
    labelKey: 'Trading Account.Trading Account',
    responsiveWidth: { desktop: { minWidth: 160 } },
    visibleOnMobile: true,
    getValue: ({ login }, { t }) => {
      const value = placeholder.NA(t, login)
      return login ? (
        <Link
          to='/dashboard/introducing-broker/reports/income'
          variant='secondary'
          state={{ TradingAccountName: login }}
        >
          {value}
        </Link>
      ) : (
        <Tooltip content={t('IB.ReportsPage.No TA created')}>{value}</Tooltip>
      )
    },
  },
  {
    id: 'ClientName',
    labelKey: 'IB.Client Name',
    minWidth: 163,
    width: 132,
    visibleOnMobile: true,
    getValue: ({ clientName }) => clientName,
  },
  {
    id: 'AccountType',
    labelKey: 'IB.Account Type',
    minWidth: 123,
    getValue: ({ isSwapFree, accountType }, { t }) =>
      placeholder.NA(t, addSwapFreePostfix(isSwapFree, accountType, t)),
  },
  {
    id: 'RegistrationDate',
    labelKey: 'IB.Registration Date',
    minWidth: 143,
    getValue: ({ registrationDate }, { t, dateFormat }) => {
      const [date, time] = formatDate(registrationDate, { formatType: dateFormat })?.split(' ') ?? [
        '',
        '',
      ]
      return <DateTime date={date} time={time} t={t} />
    },
  },
  {
    id: 'Country',
    labelKey: 'IB.Country',
    minWidth: 142,
    getValue: ({ country }) => country,
  },
  {
    id: 'TickmillCompany',
    labelKey: 'IB.Entity',
    minWidth: 123,
    getValue: ({ tickmillCompany }) =>
      tickmillCompany ? getCompanyName(tickmillCompany.id) : null,
  },
  {
    id: 'Currency',
    labelKey: 'IB.Currency',
    minWidth: 90,
    getValue: ({ currency }, { t }) => placeholder.NA(t, currency),
  },
  {
    id: 'FxmVolume',
    labelKey: 'IB.ReportsPage.Clients.Vol.FXM',
    responsiveWidth: { desktop: { minWidth: 102 } },
    align: 'right',
    isNumeric: true,
    getValue: ({ fxmVolume }, { formatMoney }) => formatMoney?.(fxmVolume),
  },
  {
    id: 'OtherVolume',
    labelKey: 'IB.ReportsPage.Clients.Vol.Other',
    responsiveWidth: { desktop: { minWidth: 126 } },
    align: 'right',
    isNumeric: true,
    getValue: ({ otherVolume }, { formatMoney }) => formatMoney?.(otherVolume),
  },
  {
    id: 'FxmAmount',
    labelKey: 'IB.ReportsPage.Clients.Commission.FXM',
    minWidth: 144,
    align: 'right',
    isNumeric: true,
    getValue: ({ fxmAmount }, { formatMoney }) => formatMoney?.(fxmAmount),
  },
  {
    id: 'OtherAmount',
    labelKey: 'IB.ReportsPage.Clients.Commission.Other',
    minWidth: 144,
    align: 'right',
    isNumeric: true,
    getValue: ({ otherAmount }, { formatMoney }) => formatMoney?.(otherAmount),
  },
  {
    id: 'SpreadRevenue',
    labelKey: 'IB.Spread Revenue',
    align: 'right',
    isNumeric: true,
    getValue: ({ spreadRevenue, accountType }, { t, formatNumber }) => {
      const isAccountTypeClassic = PlanValuesKeys.Classic === accountType
      return isAccountTypeClassic ? formatNumber(spreadRevenue ?? 0) : t('N/A')
    },
  },
  {
    id: VIEW_MORE_ARROW_COL_KEY,
    labelKey: '',
    visibleOnMobile: true,
    align: 'center',
    width: 40,
    getValue: () => null,
  },
]
